import { formatIsoDate, isValidDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { parseNumberPtBr } from '../../utils/numberUtils';

export const CRM_LEADS_LIST_API = 'crm/leads';
export const CRM_LEADS_REPORT_API = 'crm/leadsReport';
export const CRM_LEAD_TEXT_FILTER_FIELDS = ['lead_id', 'telefone', 'nome', 'email', 'cpf', 'cupom', 'source', 'campaign', 'content', 'term'];
export const CRM_LEAD_INTEGER_FILTER_FIELDS = ['cliente_id'];
export const CRM_LEAD_DEBOUNCED_FILTER_FIELDS = [...CRM_LEAD_TEXT_FILTER_FIELDS, ...CRM_LEAD_INTEGER_FILTER_FIELDS];
export const CRM_LEAD_SELECT_FILTER_FIELDS = ['vendedor_id', 'status_id', 'fase_id', 'origem_id'];
export const CRM_LEAD_TICKET_ORDER_FIELD = 'ordem_id';
export const CRM_LEAD_WON_PHASE_ID = 5;
// Com a fase travada em Conquistado, Status e Fase nao informam nada: saem da tabela.
export const CRM_LEAD_PHASE_LOCKED_HIDDEN_FIELDS = ['status', 'fase'];
export const CRM_LEAD_DATE_FILTER_RANGES = {
    cadastro: { start: 'cadastro_inicio', end: 'cadastro_fim' },
    phase_four_date: { start: 'contrato_inicio', end: 'contrato_fim' },
    phase_five_date: { start: 'conquistado_inicio', end: 'conquistado_fim' }
};
export const CRM_LEADS_INITIAL_DATE_FILTERS = Object.values(CRM_LEAD_DATE_FILTER_RANGES)
    .reduce((filters, range) => ({
        ...filters,
        [range.start]: null,
        [range.end]: null
    }), {});
export const CRM_LEADS_INITIAL_FILTERS = {
    vendedor_id: '',
    status_id: [],
    fase_id: '',
    origem_id: '',
    lead_id: '',
    cliente_id: null,
    telefone: '',
    nome: '',
    email: '',
    cpf: '',
    cupom: '',
    source: '',
    campaign: '',
    content: '',
    term: '',
    ordem_id: 0
};

export const CRM_LEAD_MARKETING_FIELDS = ['source', 'campaign', 'content', 'term'];
export const CRM_LEAD_REQUIRED_COLUMN_FIELD = 'nome';
export const CRM_LEAD_DEFAULT_VISIBLE_FIELDS = ['lead_id', 'cliente_id', 'nome', 'status', 'fase', 'ticket', 'vendedor', 'telefone', 'cpf', 'origem', 'cadastro'];
export const CRM_LEAD_COLUMN_FILTER_FIELDS = {
    fase: 'fase_id',
    origem: 'origem_id',
    status: 'status_id',
    ticket: CRM_LEAD_TICKET_ORDER_FIELD,
    vendedor: 'vendedor_id'
};

export const getCrmLeadColumnFilterField = (columnField) => CRM_LEAD_COLUMN_FILTER_FIELDS[columnField] ?? columnField;

const CRM_LEAD_COLUMNS_STORAGE_PREFIX = 'crmLeadsColumns:';

export const readCrmLeadColumnPreference = (email) => {
    try {
        const stored = window.localStorage.getItem(CRM_LEAD_COLUMNS_STORAGE_PREFIX + email);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed.filter((field) => typeof field === 'string') : null;
    } catch {
        return null;
    }
};

export const saveCrmLeadColumnPreference = (email, fields) => {
    try {
        if (fields === null) {
            window.localStorage.removeItem(CRM_LEAD_COLUMNS_STORAGE_PREFIX + email);
        } else {
            window.localStorage.setItem(CRM_LEAD_COLUMNS_STORAGE_PREFIX + email, JSON.stringify(fields));
        }
        return true;
    } catch {
        return false;
    }
};

export const toIdArray = (value) => {
    if (Array.isArray(value)) return value;
    return value === '' || value === null || value === undefined ? [] : [value];
};

export const isSameCrmLeadFilterValue = (current, initial) => {
    if (Array.isArray(current) || Array.isArray(initial)) {
        const currentIds = toIdArray(current);
        const initialIds = toIdArray(initial);
        return currentIds.length === initialIds.length && currentIds.every((id) => initialIds.includes(id));
    }

    return (current ?? '') === (initial ?? '');
};

export const countActiveCrmLeadFilters = (filters, dateFilters, defaultFilters) => {
    const changedFilters = Object.keys(defaultFilters)
        .filter((field) => !isSameCrmLeadFilterValue(filters[field], defaultFilters[field]))
        .length;
    const appliedDateRanges = Object.values(CRM_LEAD_DATE_FILTER_RANGES)
        .filter((range) => dateFilters[range.start] || dateFilters[range.end])
        .length;

    return changedFilters + appliedDateRanges;
};

export const CRM_LEAD_KANBAN_BAR_FILTER_FIELDS = ['vendedor_id', 'status_id', 'origem_id'];
export const CRM_LEAD_KANBAN_BAR_DATE_FIELD = 'cadastro';

export const getCrmLeadTableOnlyFilterFields = (filters, dateFilters, defaultFilters) => {
    const filterFields = Object.keys(defaultFilters)
        .filter((field) => !CRM_LEAD_KANBAN_BAR_FILTER_FIELDS.includes(field))
        .filter((field) => !isSameCrmLeadFilterValue(filters[field], defaultFilters[field]));
    const dateFields = Object.entries(CRM_LEAD_DATE_FILTER_RANGES)
        .filter(([field, range]) => field !== CRM_LEAD_KANBAN_BAR_DATE_FIELD && (dateFilters[range.start] || dateFilters[range.end]))
        .map(([field]) => field);

    return [...filterFields, ...dateFields];
};

export const buildCrmLeadDateFilterPayload = (dateFilters) => Object.values(CRM_LEAD_DATE_FILTER_RANGES)
    .reduce((payload, range) => {
        const startDate = dateFilters[range.start];
        const endDate = dateFilters[range.end];
        const isCompleteValidRange = isValidDate(startDate) &&
            isValidDate(endDate) &&
            startDate.getTime() <= endDate.getTime();

        payload[range.start] = isCompleteValidRange ? formatIsoDate(startDate) : '';
        payload[range.end] = isCompleteValidRange ? formatIsoDate(endDate) : '';
        return payload;
    }, {});

const CRM_LEADS_VIEW_STORAGE_PREFIX = 'crmLeadsView:';
export const CRM_LEADS_VIEW_TABLE = 'table';
export const CRM_LEADS_VIEW_KANBAN = 'kanban';
const CRM_LEADS_VIEWS = [CRM_LEADS_VIEW_TABLE, CRM_LEADS_VIEW_KANBAN];

export const readCrmLeadViewPreference = (email) => {
    try {
        const stored = window.localStorage.getItem(CRM_LEADS_VIEW_STORAGE_PREFIX + email);
        return CRM_LEADS_VIEWS.includes(stored) ? stored : null;
    } catch {
        return null;
    }
};

export const saveCrmLeadViewPreference = (email, view) => {
    try {
        window.localStorage.setItem(CRM_LEADS_VIEW_STORAGE_PREFIX + email, view);
        return true;
    } catch {
        return false;
    }
};

export const buildCrmLeadsPayload = ({ dateFilters, filters, page, pageSize }) => JSON.stringify({
    cadastro_inicio: dateFilters.cadastro_inicio,
    cadastro_fim: dateFilters.cadastro_fim,
    contrato_inicio: dateFilters.contrato_inicio,
    contrato_fim: dateFilters.contrato_fim,
    conquistado_inicio: dateFilters.conquistado_inicio,
    conquistado_fim: dateFilters.conquistado_fim,
    vendedor_id: toIdArray(filters.vendedor_id),
    status_id: toIdArray(filters.status_id),
    fase_id: toIdArray(filters.fase_id),
    origem_id: toIdArray(filters.origem_id),
    lead_id: filters.lead_id,
    cliente_id: filters.cliente_id,
    telefone: filters.telefone,
    nome: filters.nome,
    email: filters.email,
    cpf: filters.cpf,
    cupom: filters.cupom,
    source: filters.source,
    campaign: filters.campaign,
    content: filters.content,
    term: filters.term,
    ordem_id: filters.ordem_id,
    page,
    page_size: pageSize
});

export const fetchCrmLeadsPage = async ({ dateFilters, filters, page, pageSize }) => {
    const response = await fetchFlask(CRM_LEADS_LIST_API, buildCrmLeadsPayload({
        dateFilters,
        filters,
        page,
        pageSize
    }));

    return {
        ...response.data,
        totalTicket: response.data.total_ticket !== undefined || response.data.totalTicket !== undefined
            ? parseNumberPtBr(response.data.total_ticket ?? response.data.totalTicket)
            : null
    };
};

const CRM_LEAD_BY_ID_PAGE_SIZE = 50;
const CRM_LEADS_EMPTY_DATE_PAYLOAD = Object.values(CRM_LEAD_DATE_FILTER_RANGES)
    .reduce((payload, range) => ({ ...payload, [range.start]: '', [range.end]: '' }), {});

const fetchCrmLeadIdMatches = async ({ filters, page, pageSize }) => {
    const response = await fetchFlask(CRM_LEADS_LIST_API, buildCrmLeadsPayload({
        dateFilters: CRM_LEADS_EMPTY_DATE_PAYLOAD,
        filters,
        page,
        pageSize
    }));

    if (response?.status !== 'success' || !response.data) {
        throw new Error(response?.message || 'Não foi possível carregar o lead.');
    }

    return {
        items: Array.isArray(response.data.items) ? response.data.items : [],
        total: Number(response.data.total) || 0
    };
};

export const fetchCrmLeadById = async (leadId, { isPhaseLocked = false } = {}) => {
    const id = Number(leadId);
    if (!Number.isInteger(id) || id <= 0) return null;

    const filters = {
        ...CRM_LEADS_INITIAL_FILTERS,
        lead_id: String(id),
        ...(isPhaseLocked ? { fase_id: CRM_LEAD_WON_PHASE_ID } : {})
    };
    const isSameLead = (item) => Number(item.lead_id) === id;

    const firstPage = await fetchCrmLeadIdMatches({ filters, page: 1, pageSize: CRM_LEAD_BY_ID_PAGE_SIZE });
    const match = firstPage.items.find(isSameLead);
    if (match || firstPage.total <= firstPage.items.length) return match ?? null;

    const lastPage = await fetchCrmLeadIdMatches({ filters, page: firstPage.total, pageSize: 1 });
    return lastPage.items.find(isSameLead) ?? null;
};

export const fetchCrmLeadsReport = ({ dateFilters, filters, page, pageSize }) => fetchFlask(
    CRM_LEADS_REPORT_API,
    buildCrmLeadsPayload({
        dateFilters,
        filters,
        page,
        pageSize
    })
);
